import type { PointsBreakdownState, PointsPlayerBreakdown } from './PointsBreakdown';
import type {
  PointsCompareState,
  PointsPreviewResult,
  PointsPreviewState,
} from './PointsPreviewPane';

/**
 * The preview pane's state machine, in one place.
 *
 * Both consoles fetch the preview the same way — a query keyed on the definition, keeping the
 * previous result while the next one is worked out — and the mapping from that to what the pane
 * renders has an order that matters:
 *
 * 1. **Blocked** beats everything. A definition with errors was never sent, so a stale board next
 *    to an invalid form would be answering a question nobody asked.
 * 2. **Idle** next: there is nothing to score against yet, which is not a failure.
 * 3. **Error over loading**, and both carry the previous board. A rate-limited call should not
 *    wipe the standings an operator was reading — it should say so above them.
 */
export function toPreviewState(input: {
  /** The definition has problems the server would refuse. */
  blocked: boolean;
  /** Set when there is nothing to preview against — no room chosen, typically. */
  idleMessage: string | null;
  data: PointsPreviewResult | undefined;
  isFetching: boolean;
  error: string | null;
}): PointsPreviewState {
  if (input.blocked) return { status: 'blocked' };
  if (input.idleMessage !== null) return { status: 'idle', message: input.idleMessage };
  if (input.error !== null) {
    return { status: 'error', message: input.error, previous: input.data ?? null };
  }
  if (input.isFetching) return { status: 'loading', previous: input.data ?? null };
  if (input.data) return { status: 'ready', result: input.data };
  return { status: 'loading', previous: null };
}

/**
 * The same mapping for §9.8's compare board, and one rule it does not share with the one above.
 *
 * <b>A comparison holds no previous result.</b> The board above keeps the last calculation on
 * screen while the next is worked out, because a table blanking on every keystroke is unreadable.
 * Movement cannot do that: it is a statement *about* two boards, and pairing the current one with
 * a comparison computed against something else reports players moving who have not moved. So while
 * either side is settling there is no comparison at all — which is why `settling` takes the
 * *draft's* fetch state too, not only the published version's.
 */
export function compareState(input: {
  on: boolean;
  error: string | null;
  data: PointsPreviewResult | undefined;
  /** Either board still being worked out. */
  settling: boolean;
}): PointsCompareState {
  if (!input.on) return { status: 'off' };
  if (input.error !== null) return { status: 'error', message: input.error };
  if (input.settling || !input.data) return { status: 'loading' };
  return { status: 'ready', result: input.data };
}

/**
 * §7.4's drill-down, mapped the same way — and with the one distinction the payload makes that a
 * `data ?? null` would flatten.
 *
 * <b>A `null` breakdown on a successful response is an answer</b>: the request named a player the
 * previewed board does not hold, which is a thing an operator checks for deliberately. It is not
 * "still loading" and it is not an error, and the panel words it as neither. So `undefined` (no
 * response yet) and `null` (a response saying nothing was found) are kept apart all the way to the
 * screen rather than being collapsed at the edge of the query.
 */
export function breakdownState(input: {
  error: string | null;
  data: PointsPlayerBreakdown | null | undefined;
  isFetching: boolean;
}): PointsBreakdownState {
  if (input.error !== null) return { status: 'error', message: input.error };
  if (input.data === undefined || input.isFetching) return { status: 'loading' };
  return { status: 'ready', result: input.data };
}
